// src/components/APanel/QuestionEditor/TheoryImportPopup.jsx
//
// Întrebări → Unelte → „Import teorie” (P1–P24), în sub-popup. Fost panoul de import din
// Corectare teorie. Formatul fișierului se recunoaște singur:
//   · auto_test_AB — întâi verificarea fără scriere (dry-run), apoi „Aplică”;
//   · biletele oficiale RO / RU — textele, răspunsurile și răspunsul corect, pe poziții;
//   · patch-ul RU (reordonarea răspunsurilor RU sau textele RO + RU) — fiecare întrebare
//     se revizuiește: „Schimbăm” / „Nu schimbăm”, sau „Confirmă tot” (cu „Oprește”).
// Dedesubt, „Jurnal aplicare”. Răspunsul corect al teoriei e pe server de la 0: importul
// oficial și patch-ul îl scriu așa (examQuestionFormat, ticketType PRACTICE).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
   getExamQuestionById,
   getExamTicketById,
   getExamTicketQuestions,
   loadAllPracticeTickets,
   updateExamQuestion,
} from "../../../api/examAdminService";
import { buildExamQuestionPutPayload, normalizeExamQuestionRecord } from "../../../utils/examQuestionFormat";
import {
   applyRuPatchToQuestion,
   buildQuestionPreviewDiff,
   parseTheoryRuReorderPatch,
} from "../../../utils/theoryRuReorderPatch";
import {
   buildTheoryOfficialApplicationPlan,
   isOfficialTheoryImport,
   parseTheoryOfficialImport,
   resolvePracticeTicket,
   THEORY_TICKETS_MAX,
} from "../../../utils/theoryOfficialTicketsImport";
import {
   applyAutoTestAbUpdates,
   AUTO_TEST_AB_RULE,
   isAutoTestAbImport,
   loadPracticeSiteSnapshot,
   parseAutoTestAbImport,
   runAutoTestAbCorrection,
} from "../../../utils/autoTestAbImport";
import { ButtonUI, CardUI, ChoiceUI, ListUI, StatusUI, TagUI } from "../../UI";
import { ImportLog, ImportWarnings, JsonFileButton, readJsonFile, safeList } from "./importTools";

const PRACTICE = { ticketType: "PRACTICE" };
const APPLY_DELAY_MS = 120;
const PREVIEW_CONCURRENCY = 8;

const DECISION = {
   PENDING: "pending",
   APPLIED: "applied",
   SKIPPED: "skipped",
   ERROR: "error",
};

const DECISION_TAG = {
   [DECISION.PENDING]: { tone: "info", label: "De decis" },
   [DECISION.APPLIED]: { tone: "success", label: "Schimbat" },
   [DECISION.SKIPPED]: { tone: "muted", label: "Păstrat" },
   [DECISION.ERROR]: { tone: "danger", label: "Eroare" },
};

function sleep(ms) {
   return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function confidenceTone(value) {
   const text = String(value ?? "").toLowerCase();
   if (text.includes("low")) return "danger-soft";
   if (text.includes("medium")) return "warning";
   if (text.includes("high") || text.includes("reviewed")) return "success";
   return "neutral";
}

function patchTypeLabel(type) {
   if (type === "auto_test_AB" || String(type || "").includes("auto_test")) return "auto_test_AB oficial";
   if (type === "official_practice_tickets_ro_ru_correct_answers") return "bilete oficiale P1–P24";
   if (type?.includes?.("official")) return "import oficial RO/RU";
   if (type === "full_ro_ru_answer_text_repair") return "corecție text RO + RU";
   if (type === "ro_ru_answer_reorder_patch") return "reordonare RU";
   return type || "patch teorie";
}

function applySuccessMessage(patch) {
   if (patch?.patchKind === "text_repair") {
      return "RO și RU actualizate. Ordinea răspunsurilor neschimbată.";
   }
   return "RU actualizat. RO neschimbat.";
}

function isReviewableItem(item) {
   return Boolean(!item?.loadError && !item?.diff?.error && item?.diff?.hasChanges);
}

async function mapWithConcurrency(items, limit, mapper) {
   const results = new Array(items.length);
   let cursor = 0;

   async function worker() {
      while (cursor < items.length) {
         const index = cursor;
         cursor += 1;
         results[index] = await mapper(items[index], index);
      }
   }

   await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => worker()));
   return results;
}

// întrebarea de pe server, cu răspunsul corect citit de la 0 (teoria)
async function loadPracticeQuestion(questionId) {
   const current = normalizeExamQuestionRecord(await getExamQuestionById(questionId), 0, PRACTICE);
   if (!current) throw new Error("Întrebarea nu a putut fi normalizată.");
   return current;
}

async function applyQuestionPatch(item) {
   const current = await loadPracticeQuestion(item.patch.questionId);
   const nextDraft = applyRuPatchToQuestion(current, item.patch);
   await updateExamQuestion(item.patch.questionId, buildExamQuestionPutPayload(nextDraft, PRACTICE));
}

// răspunsurile unei coloane din comparație; răspunsul corect e marcat
function AnswerColumn({ title, answers, lang, changed, correct }) {
   return (
      <ListUI title={title}>
         {answers.map((answer, index) => (
            <ListUI.Item
               key={index}
               compact
               tone={changed?.has(index) ? "progress" : undefined}
               title={`${index + 1}. ${answer[lang] || "—"}`}
               meta={index + 1 === correct ? "corect" : undefined}
            />
         ))}
      </ListUI>
   );
}

// o corecție din patch: întrebarea, acum și după, RO și RU
function PreviewCard({ item, decision }) {
   const { patch, diff, loadError } = item;
   const before = diff?.before;
   const after = diff?.after;
   const tag = DECISION_TAG[decision] || DECISION_TAG[DECISION.PENDING];
   const correct = diff?.correctAnswer;

   return (
      <div className="questionsImport__preview">
         <div className="questionsImport__previewHead">
            <strong>
               {patch.ticketCode || "—"} · {patch.bKey || "—"} · #{patch.questionId}
            </strong>
            <TagUI.Group>
               <TagUI tone={tag.tone}>{tag.label}</TagUI>
               {patch.confidence && <TagUI tone={confidenceTone(patch.confidence)}>{patch.confidence}</TagUI>}
            </TagUI.Group>
         </div>
         {patch.short && <StatusUI>{patch.short}</StatusUI>}
         {loadError && <StatusUI tone="error">{loadError}</StatusUI>}

         {!loadError && before && (
            <>
               <ListUI>
                  <ListUI.Item compact title={before.textRo || "—"} meta="RO" />
                  <ListUI.Item compact title={before.textRu || "—"} meta="RU" />
               </ListUI>
               <div className="questionsImport__compare">
                  <AnswerColumn title="Acum · Română" answers={before.answers} lang="ro" correct={correct} />
                  <AnswerColumn title="Acum · Rusă" answers={before.answers} lang="ru" correct={correct} />
                  <AnswerColumn
                     title="După · Română"
                     answers={after?.answers || before.answers}
                     lang="ro"
                     changed={new Set(diff?.changedRoAnswerIndexes || [])}
                     correct={correct}
                  />
                  {diff?.error ? (
                     <StatusUI look="card" tone="error" title="După · Rusă">
                        {diff.error}
                     </StatusUI>
                  ) : (
                     <AnswerColumn
                        title="După · Rusă"
                        answers={after?.answers || []}
                        lang="ru"
                        changed={new Set(diff?.changedAnswerIndexes || [])}
                        correct={correct}
                     />
                  )}
               </div>
            </>
         )}
      </div>
   );
}

export default function TheoryImportPopup({ onDone }) {
   const [notice, setNotice] = useState(null);
   const [fileName, setFileName] = useState("");
   const [importMode, setImportMode] = useState(null);
   // patch RU
   const [parsedPatch, setParsedPatch] = useState(null);
   const [previewItems, setPreviewItems] = useState([]);
   const [previewLoading, setPreviewLoading] = useState(false);
   const [previewProgress, setPreviewProgress] = useState(null);
   const [selectedTicket, setSelectedTicket] = useState("all");
   const [currentIndex, setCurrentIndex] = useState(0);
   const [decisions, setDecisions] = useState({});
   const [itemBusy, setItemBusy] = useState(false);
   const [bulkApplying, setBulkApplying] = useState(false);
   const [applyProgress, setApplyProgress] = useState(null);
   const [applyResults, setApplyResults] = useState([]);
   // biletele oficiale
   const [officialBundle, setOfficialBundle] = useState(null);
   const [officialApplying, setOfficialApplying] = useState(false);
   const [officialApplyProgress, setOfficialApplyProgress] = useState(null);
   // auto_test_AB
   const [autoTestAbBundle, setAutoTestAbBundle] = useState(null);
   const [autoTestAbReport, setAutoTestAbReport] = useState(null);
   const [autoTestAbDryRunLoading, setAutoTestAbDryRunLoading] = useState(false);
   const [autoTestAbApplying, setAutoTestAbApplying] = useState(false);
   const [autoTestAbApplyProgress, setAutoTestAbApplyProgress] = useState(null);
   const siteSnapshotRef = useRef([]);
   const abortRef = useRef(false);

   // după o întrebare aplicată singură, editorul se reîncarcă la închiderea popup-ului
   // (după aplicările în bloc, imediat)
   const onDoneRef = useRef(onDone);
   onDoneRef.current = onDone;
   const reloadOnCloseRef = useRef(false);
   useEffect(
      () => () => {
         if (reloadOnCloseRef.current) onDoneRef.current?.();
      },
      [],
   );
   const reloadNow = useCallback(() => {
      reloadOnCloseRef.current = false;
      onDoneRef.current?.();
   }, []);

   const pushNotice = useCallback((text, tone = "info") => setNotice({ text: String(text ?? ""), tone }), []);

   const getDecision = useCallback(
      (item) => {
         const id = item?.patch?.questionId;
         if (!id) return DECISION.PENDING;
         if (decisions[id]) return decisions[id];
         if (item.loadError || item.diff?.error) return DECISION.ERROR;
         if (!item.diff?.hasChanges) return DECISION.SKIPPED;
         return DECISION.PENDING;
      },
      [decisions],
   );

   // ── patch RU: previzualizarea (întrebarea de acum și cum devine) ──
   const loadPreview = useCallback(
      async (parsed) => {
         const patches = parsed?.questionPatches || [];
         if (!patches.length) {
            setPreviewItems([]);
            return;
         }

         abortRef.current = false;
         setPreviewLoading(true);
         setPreviewProgress({ current: 0, total: patches.length });
         setPreviewItems([]);
         setApplyResults([]);
         setDecisions({});
         setCurrentIndex(0);

         let completed = 0;

         try {
            const rows = await mapWithConcurrency(patches, PREVIEW_CONCURRENCY, async (patch) => {
               if (abortRef.current) return { patch, diff: null, loadError: "Anulat." };

               try {
                  const current = await loadPracticeQuestion(patch.questionId);
                  const diff = { ...buildQuestionPreviewDiff(current, patch), correctAnswer: current.correctAnswer };
                  completed += 1;
                  setPreviewProgress({ current: completed, total: patches.length });
                  return { patch, diff, loadError: null };
               } catch (error) {
                  completed += 1;
                  setPreviewProgress({ current: completed, total: patches.length });
                  return { patch, diff: null, loadError: error?.message || "Eroare la încărcare." };
               }
            });

            if (!abortRef.current) {
               setPreviewItems(rows);
               const ready = rows.filter(isReviewableItem).length;
               pushNotice(`Previzualizare gata: ${ready} întrebări de revizuit din ${rows.length}.`, "success");
            }
         } catch (error) {
            pushNotice(error?.message || "Nu am putut genera previzualizarea.", "error");
         } finally {
            setPreviewLoading(false);
            setPreviewProgress(null);
         }
      },
      [pushNotice],
   );

   // ── auto_test_AB: întrebările de pe site și verificarea fără scriere ──
   const loadSiteSnapshot = useCallback(async () => {
      const entries = await loadPracticeSiteSnapshot({
         loadTickets: loadAllPracticeTickets,
         loadTicketById: getExamTicketById,
         loadTicketQuestions: getExamTicketQuestions,
         maxTickets: THEORY_TICKETS_MAX,
      });
      siteSnapshotRef.current = entries;
      return entries;
   }, []);

   const runAutoTestAbDryRun = useCallback(
      async (bundle) => {
         setAutoTestAbDryRunLoading(true);
         setAutoTestAbReport(null);

         try {
            const siteEntries = await loadSiteSnapshot();
            if (!siteEntries.length) {
               throw new Error("Nu am găsit întrebări pe site pentru P1–P24. Verifică biletele Practice (#246–#269).");
            }
            const result = runAutoTestAbCorrection({ bundle, siteEntries, apply: false });
            setAutoTestAbReport(result);

            const { report } = result;
            pushNotice(
               `Dry-run: ${report.foundInSite}/${report.officialTotal} găsite, ${report.wouldUpdate} de actualizat, ${report.missingInSite} lipsă pe site, ${report.notInOfficialFile} doar pe site.`,
               report.missingInSite || report.notInOfficialFile ? "warning" : "success",
            );
         } catch (error) {
            setAutoTestAbReport(null);
            pushNotice(error?.message || "Nu am putut rula dry-run pentru auto_test_AB.", "error");
         } finally {
            setAutoTestAbDryRunLoading(false);
         }
      },
      [loadSiteSnapshot, pushNotice],
   );

   // ── fișierul: se recunoaște formatul ──
   const pickFile = useCallback(
      async (file) => {
         try {
            const json = await readJsonFile(file);

            setFileName(file.name);
            setPreviewItems([]);
            setApplyResults([]);
            setSelectedTicket("all");
            setCurrentIndex(0);
            setDecisions({});
            setOfficialBundle(null);
            setAutoTestAbBundle(null);
            setAutoTestAbReport(null);
            setParsedPatch(null);

            if (isAutoTestAbImport(json)) {
               const bundle = parseAutoTestAbImport(json);
               if (!bundle.items.length) {
                  throw new Error(bundle.errors[0] || "Fișierul auto_test_AB nu conține întrebări valide.");
               }

               setImportMode("autoTestAb");
               setAutoTestAbBundle(bundle);
               pushNotice(
                  `Import auto_test_AB: ${bundle.summary.officialTotal} întrebări, ${bundle.summary.ticketsTotal} bilete. Se rulează dry-run…`,
                  bundle.errors.length ? "warning" : "loading",
               );
               await runAutoTestAbDryRun(bundle);
               return;
            }

            if (isOfficialTheoryImport(json)) {
               const official = parseTheoryOfficialImport(json);
               if (!official.tickets.length) {
                  throw new Error(official.errors[0] || "Fișierul oficial nu conține bilete aplicabile.");
               }

               setImportMode("official");
               setOfficialBundle(official);

               const reviewNote = official.summary.reviewCount > 0 ? ` · ${official.summary.reviewCount} de verificat` : "";
               pushNotice(
                  `Import oficial: ${official.summary.ticketsTotal} bilete (${official.summary.ticketRange}), ${official.summary.questionsTotal} întrebări${reviewNote}.`,
                  official.errors.length || official.summary.reviewCount ? "warning" : "success",
               );
               return;
            }

            const parsed = parseTheoryRuReorderPatch(json);
            setImportMode("patch");
            setParsedPatch(parsed);

            if (!parsed.questionPatches.length) {
               pushNotice(parsed.errors[0] || "Fișierul nu conține corecții aplicabile.", "error");
               return;
            }

            if (parsed.errors.length) {
               pushNotice(`Import: ${parsed.questionPatches.length} corecții, ${parsed.errors.length} avertismente.`, "warning");
            } else {
               pushNotice(
                  `Import reușit: ${parsed.questionPatches.length} corecții (${patchTypeLabel(parsed.type)}).`,
                  "success",
               );
            }

            await loadPreview(parsed);
         } catch (error) {
            setFileName("");
            setParsedPatch(null);
            setOfficialBundle(null);
            setAutoTestAbBundle(null);
            setAutoTestAbReport(null);
            setImportMode(null);
            setPreviewItems([]);
            pushNotice(error?.message || "Fișier JSON invalid sau structură necunoscută.", "error");
         }
      },
      [loadPreview, pushNotice, runAutoTestAbDryRun],
   );

   // ── auto_test_AB: aplicarea ──
   const applyAutoTestAb = useCallback(async () => {
      if (!autoTestAbBundle?.items?.length || autoTestAbApplying) return;
      if (!autoTestAbReport?.updates?.length) {
         pushNotice("Dry-run: nu există modificări de aplicat.", "warning");
         return;
      }

      setAutoTestAbApplying(true);
      setAutoTestAbApplyProgress({ current: 0, total: autoTestAbReport.updates.length });
      let applied = 0;

      try {
         const siteEntries = siteSnapshotRef.current.length > 0 ? siteSnapshotRef.current : await loadSiteSnapshot();

         const planned = runAutoTestAbCorrection({ bundle: autoTestAbBundle, siteEntries, apply: false });
         const log = await applyAutoTestAbUpdates(planned.updates, updateExamQuestion, {
            onProgress: setAutoTestAbApplyProgress,
         });

         applied = log.filter((row) => row.status === "ok").length;
         const failed = log.filter((row) => row.status === "error").length;

         const finalReport = runAutoTestAbCorrection({ bundle: autoTestAbBundle, siteEntries, apply: true });
         finalReport.report.updated = applied;
         setAutoTestAbReport(finalReport);
         setApplyResults(log);

         if (failed) {
            pushNotice(`Aplicare cu erori: ${applied} reușite, ${failed} eșuate.`, "warning");
         } else {
            pushNotice(`Aplicat: ${applied} întrebări actualizate.`, "success");
         }
      } catch (error) {
         pushNotice(error?.message || "Nu am putut aplica importul auto_test_AB.", "error");
      } finally {
         setAutoTestAbApplying(false);
         setAutoTestAbApplyProgress(null);
         if (applied > 0) reloadNow();
      }
   }, [autoTestAbApplying, autoTestAbBundle, autoTestAbReport, loadSiteSnapshot, pushNotice, reloadNow]);

   // ── biletele oficiale: aplicarea, bilet cu bilet ──
   const applyOfficial = useCallback(async () => {
      if (!officialBundle?.tickets?.length || officialApplying) return;

      setOfficialApplying(true);
      setOfficialApplyProgress({ current: 0, total: officialBundle.tickets.length });

      const log = [];
      let appliedQuestions = 0;

      try {
         const dbTickets = await loadAllPracticeTickets();
         if (!dbTickets.length) throw new Error("Nu am găsit biletele de teorie P1–P24 în backend.");

         let ticketStep = 0;
         for (const importTicket of officialBundle.tickets) {
            ticketStep += 1;
            setOfficialApplyProgress({
               current: ticketStep,
               total: officialBundle.tickets.length,
               label: importTicket.ticketCode,
            });

            const dbTicket = resolvePracticeTicket(importTicket, dbTickets);
            if (!dbTicket?.id) {
               log.push({ status: "error", target: importTicket.ticketCode, message: "Biletul nu a fost găsit în backend." });
               continue;
            }

            const ticketRaw = await getExamTicketById(dbTicket.id);
            const questionsResponse = await getExamTicketQuestions(dbTicket.id);
            const existingQuestions = safeList(questionsResponse?.items)
               .map((row, index) => normalizeExamQuestionRecord(row, index, PRACTICE))
               .filter(Boolean);

            const questionIds =
               safeList(ticketRaw?.questionIds) ||
               safeList(ticketRaw?.data?.questionIds) ||
               existingQuestions.map((question) => question.id);

            const plan = buildTheoryOfficialApplicationPlan(importTicket, existingQuestions, questionIds);

            let ticketApplied = 0;
            for (const update of plan.updates) {
               if (update.status !== "ready") continue;
               await updateExamQuestion(update.questionId, update.payload);
               ticketApplied += 1;
               appliedQuestions += 1;
            }

            log.push({
               status: plan.missingCount ? "warning" : "ok",
               target: `${importTicket.ticketCode} · #${dbTicket.id}`,
               message: plan.missingCount
                  ? `${ticketApplied} actualizate, ${plan.missingCount} lipsă.`
                  : `${ticketApplied} întrebări actualizate.`,
            });
         }

         const failed = log.filter((row) => row.status === "error").length;
         if (failed) {
            pushNotice(`Import parțial: ${appliedQuestions} întrebări, ${failed} bilete negăsite.`, "warning");
         } else {
            pushNotice(`Import aplicat: ${appliedQuestions} întrebări actualizate.`, "success");
         }
      } catch (error) {
         pushNotice(error?.message || "Nu am putut aplica importul oficial.", "error");
      } finally {
         setApplyResults(log);
         setOfficialApplying(false);
         setOfficialApplyProgress(null);
         if (appliedQuestions > 0) reloadNow();
      }
   }, [officialApplying, officialBundle, pushNotice, reloadNow]);

   // ── patch RU: revizuirea ──
   const ticketTabs = useMemo(() => {
      const codes = new Set();
      previewItems.forEach((item) => item?.patch?.ticketCode && codes.add(item.patch.ticketCode));
      return [
         "all",
         ...Array.from(codes).sort((a, b) => {
            const na = Number(String(a).replace(/^P/i, ""));
            const nb = Number(String(b).replace(/^P/i, ""));
            if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
            return String(a).localeCompare(String(b));
         }),
      ];
   }, [previewItems]);

   const visiblePreviewItems = useMemo(
      () => (selectedTicket === "all" ? previewItems : previewItems.filter((item) => item?.patch?.ticketCode === selectedTicket)),
      [previewItems, selectedTicket],
   );

   useEffect(() => {
      setCurrentIndex(0);
   }, [selectedTicket]);

   useEffect(() => {
      if (currentIndex >= visiblePreviewItems.length && visiblePreviewItems.length) {
         setCurrentIndex(Math.max(0, visiblePreviewItems.length - 1));
      }
   }, [currentIndex, visiblePreviewItems.length]);

   const currentItem = visiblePreviewItems[currentIndex] || null;
   const currentDecision = currentItem ? getDecision(currentItem) : DECISION.PENDING;
   const currentReviewable = currentItem ? isReviewableItem(currentItem) : false;

   const reviewStats = useMemo(() => {
      const stats = { pending: 0, applied: 0, skipped: 0, errors: 0, total: previewItems.length };
      previewItems.forEach((item) => {
         const status = getDecision(item);
         if (status === DECISION.APPLIED) stats.applied += 1;
         else if (status === DECISION.SKIPPED) stats.skipped += 1;
         else if (status === DECISION.ERROR) stats.errors += 1;
         else if (isReviewableItem(item)) stats.pending += 1;
      });
      return stats;
   }, [getDecision, previewItems]);

   const pendingItems = useMemo(
      () => visiblePreviewItems.filter((item) => isReviewableItem(item) && getDecision(item) === DECISION.PENDING),
      [getDecision, visiblePreviewItems],
   );

   const goToNextPending = useCallback(() => {
      if (!visiblePreviewItems.length) return;

      for (let offset = 1; offset <= visiblePreviewItems.length; offset += 1) {
         const index = (currentIndex + offset) % visiblePreviewItems.length;
         const item = visiblePreviewItems[index];
         if (isReviewableItem(item) && getDecision(item) === DECISION.PENDING) {
            setCurrentIndex(index);
            return;
         }
      }

      if (currentIndex < visiblePreviewItems.length - 1) setCurrentIndex((value) => value + 1);
   }, [currentIndex, getDecision, visiblePreviewItems]);

   const markDecision = useCallback((questionId, status) => {
      setDecisions((prev) => ({ ...prev, [questionId]: status }));
   }, []);

   const skipCurrent = useCallback(() => {
      if (!currentItem) return;
      markDecision(currentItem.patch.questionId, DECISION.SKIPPED);
      goToNextPending();
   }, [currentItem, goToNextPending, markDecision]);

   const applyCurrent = useCallback(async () => {
      if (!currentItem || !currentReviewable || itemBusy || bulkApplying) return;

      setItemBusy(true);
      try {
         await applyQuestionPatch(currentItem);
         markDecision(currentItem.patch.questionId, DECISION.APPLIED);
         setApplyResults((prev) => [
            {
               status: "ok",
               target: `${currentItem.patch.ticketCode || "Q"} · #${currentItem.patch.questionId}`,
               message: applySuccessMessage(currentItem.patch),
            },
            ...prev,
         ]);
         reloadOnCloseRef.current = true;
         goToNextPending();
      } catch (error) {
         markDecision(currentItem.patch.questionId, DECISION.ERROR);
         pushNotice(error?.message || "Nu am putut aplica modificarea pentru această întrebare.", "error");
      } finally {
         setItemBusy(false);
      }
   }, [bulkApplying, currentItem, currentReviewable, goToNextPending, itemBusy, markDecision, pushNotice]);

   const applyAll = useCallback(async () => {
      if (!pendingItems.length || bulkApplying || itemBusy) {
         pushNotice("Nu mai sunt întrebări în așteptare.", "warning");
         return;
      }

      abortRef.current = false;
      setBulkApplying(true);
      setApplyResults([]);

      const log = [];
      let step = 0;
      setApplyProgress({ current: 0, total: pendingItems.length, label: "Pornire…" });

      try {
         for (const item of pendingItems) {
            if (abortRef.current) break;
            step += 1;
            const patch = item.patch;
            const target = `${patch.ticketCode || "Q"} · #${patch.questionId}`;

            setApplyProgress({ current: step, total: pendingItems.length, label: target });

            try {
               await applyQuestionPatch(item);
               markDecision(patch.questionId, DECISION.APPLIED);
               log.push({ status: "ok", target, message: applySuccessMessage(patch) });
            } catch (error) {
               markDecision(patch.questionId, DECISION.ERROR);
               log.push({ status: "error", target, message: error?.message || "Eroare necunoscută." });
            }

            await sleep(APPLY_DELAY_MS);
         }

         setApplyResults(log);
         const failed = log.filter((row) => row.status === "error").length;
         const applied = log.filter((row) => row.status === "ok").length;

         if (failed) pushNotice(`Finalizat cu erori: ${applied} reușite, ${failed} eșuate.`, "warning");
         else pushNotice(`Modificări aplicate: ${applied} întrebări actualizate.`, "success");
      } catch (error) {
         pushNotice(error?.message || "Aplicarea s-a oprit cu eroare.", "error");
      } finally {
         setBulkApplying(false);
         setApplyProgress(null);
         if (log.some((row) => row.status === "ok")) reloadNow();
      }
   }, [bulkApplying, itemBusy, markDecision, pendingItems, pushNotice, reloadNow]);

   const stopRun = useCallback(() => {
      abortRef.current = true;
   }, []);

   const busy = itemBusy || bulkApplying || officialApplying || autoTestAbDryRunLoading || autoTestAbApplying;
   const isTextRepair = pendingItems.some((item) => item?.patch?.patchKind === "text_repair");
   const progress = autoTestAbApplyProgress || officialApplyProgress || applyProgress || null;
   const fileType =
      importMode === "autoTestAb"
         ? "auto_test_AB (potrivire după poziția în bilet)"
         : importMode === "official"
           ? patchTypeLabel(officialBundle?.type)
           : parsedPatch?.type
             ? patchTypeLabel(parsedPatch.type)
             : "";
   const autoReport = autoTestAbReport?.report;

   return (
      <CardUI
         level="popup"
         title="Import teorie"
         description="P1–P24. Biletele se găsesc după nume (ca la elev), întrebările după poziția în bilet. Formatul fișierului se recunoaște singur."
      >
         <ButtonUI.Row align="start">
            <JsonFileButton onFile={pickFile} disabled={previewLoading || busy} />

            {importMode === "autoTestAb" && (
               <>
                  <ButtonUI
                     loading={autoTestAbDryRunLoading}
                     disabled={!autoTestAbBundle || busy}
                     onClick={() => autoTestAbBundle && runAutoTestAbDryRun(autoTestAbBundle)}
                  >
                     Rulează din nou dry-run
                  </ButtonUI>
                  <ButtonUI
                     variant="primary"
                     confirm="Da, aplică"
                     confirmTitle="Nu se creează întrebări noi; imaginile rămân ale site-ului."
                     loading={autoTestAbApplying}
                     disabled={!autoTestAbReport?.updates?.length || busy}
                     onClick={applyAutoTestAb}
                  >
                     Aplică ({autoReport?.wouldUpdate || 0})
                  </ButtonUI>
               </>
            )}

            {importMode === "official" && (
               <ButtonUI
                  variant="primary"
                  confirm="Da, aplică"
                  confirmTitle={`Se modifică ${officialBundle?.summary?.questionsTotal || 0} întrebări din ${officialBundle?.summary?.ticketsTotal || 0} bilete.`}
                  loading={officialApplying}
                  disabled={!officialBundle?.tickets?.length || busy}
                  onClick={applyOfficial}
               >
                  Aplică importul
               </ButtonUI>
            )}
         </ButtonUI.Row>

         <StatusUI tone={fileName ? "neutral" : "empty"}>
            {fileName
               ? `Fișier: ${fileName}${fileType ? ` · ${fileType}` : ""}`
               : "Formate: auto_test_AB.json, biletele oficiale RO / RU sau patch-ul RU (patchesByTicket / tickets)."}
         </StatusUI>

         {notice && <StatusUI tone={notice.tone}>{notice.text}</StatusUI>}

         {/* auto_test_AB */}
         {importMode === "autoTestAb" && (
            <StatusUI look="card" tone="info" title="Reguli import (auto_test_AB)">
               {autoTestAbBundle?.rule || AUTO_TEST_AB_RULE} Nu se creează întrebări noi; imaginile rămân ale site-ului.
            </StatusUI>
         )}
         {importMode === "autoTestAb" && autoReport && (
            <ListUI columns={autoReport.answerCountMismatch > 0 ? 5 : 4}>
               <ListUI.Item layout="stat" title="Găsite" meta={`${autoReport.foundInSite}/${autoReport.officialTotal}`} />
               <ListUI.Item
                  layout="stat"
                  title={autoTestAbReport.apply ? "Actualizate" : "De actualizat"}
                  meta={autoTestAbReport.apply ? autoReport.updated : autoReport.wouldUpdate}
               />
               <ListUI.Item layout="stat" title="Lipsă pe site" meta={autoReport.missingInSite} />
               <ListUI.Item layout="stat" title="Doar pe site" meta={autoReport.notInOfficialFile} />
               {autoReport.answerCountMismatch > 0 && (
                  <ListUI.Item layout="stat" title="Nr. răspunsuri diferit" meta={autoReport.answerCountMismatch} tone="bad" />
               )}
            </ListUI>
         )}
         {importMode === "autoTestAb" && autoTestAbReport?.rows?.length > 0 && (
            <details className="questionsImport__details">
               <summary>Raport dry-run / aplicare · {autoTestAbReport.rows.length}</summary>
               <ListUI>
                  {autoTestAbReport.rows.slice(0, 40).map((row, index) => (
                     <ListUI.Item
                        key={`${row.status}-${row.bKey}-${index}`}
                        compact
                        title={`${row.status} · ${row.ticketCode} ${row.bKey}${row.questionId ? ` · #${row.questionId}` : ""}`}
                        detail={row.message}
                     />
                  ))}
                  {autoTestAbReport.rows.length > 40 && (
                     <ListUI.Empty>… și încă {autoTestAbReport.rows.length - 40} rânduri</ListUI.Empty>
                  )}
               </ListUI>
            </details>
         )}
         {importMode === "autoTestAb" && <ImportWarnings items={autoTestAbBundle?.errors} />}

         {/* biletele oficiale */}
         {importMode === "official" && (
            <StatusUI look="card" tone="info" title="Reguli import">
               {officialBundle?.rule ||
                  "Ordinea întrebărilor și a răspunsurilor este din documentul oficial RO. Rusa este aliniată după română. Se actualizează textele și răspunsul corect."}{" "}
               Ordinea din fișier se păstrează.
            </StatusUI>
         )}
         {importMode === "official" && officialBundle?.summary && (
            <ListUI columns={3}>
               <ListUI.Item
                  layout="stat"
                  title={`Bilete (${officialBundle.summary.ticketRange || "P1–P24"})`}
                  meta={officialBundle.summary.ticketsTotal}
               />
               <ListUI.Item layout="stat" title="Întrebări" meta={officialBundle.summary.questionsTotal} />
               <ListUI.Item
                  layout="stat"
                  title="De verificat"
                  meta={officialBundle.summary.reviewCount}
                  tone={officialBundle.summary.reviewCount > 0 ? "bad" : undefined}
               />
            </ListUI>
         )}
         {importMode === "official" && <ImportWarnings items={officialBundle?.errors} />}

         {/* patch-ul RU */}
         {importMode === "patch" && parsedPatch?.summary && (
            <ListUI columns={3}>
               <ListUI.Item
                  layout="stat"
                  title="Corecții"
                  meta={parsedPatch.summary.questionsPatched ?? parsedPatch.summary.correctionsTotal ?? reviewStats.total}
               />
               <ListUI.Item layout="stat" title="Bilete" meta={parsedPatch.summary.ticketsTotal ?? 24} />
               {parsedPatch.summary.lowReviewNeeded != null && (
                  <ListUI.Item layout="stat" title="De verificat" meta={parsedPatch.summary.lowReviewNeeded} tone="bad" />
               )}
            </ListUI>
         )}
         {importMode === "patch" && previewLoading && previewProgress && (
            <StatusUI tone="loading">
               Se încarcă previzualizarea… {previewProgress.current}/{previewProgress.total}
            </StatusUI>
         )}
         {importMode === "patch" && <ImportWarnings items={parsedPatch?.errors} />}

         {progress && (
            <StatusUI tone="loading">
               Se aplică: {progress.current}/{progress.total}
               {progress.label ? ` · ${progress.label}` : ""}
            </StatusUI>
         )}

         {importMode === "patch" && previewItems.length > 0 && (
            <>
               <ListUI columns={4}>
                  <ListUI.Item layout="stat" title="De decis" meta={reviewStats.pending} />
                  <ListUI.Item layout="stat" title="Schimbate" meta={reviewStats.applied} tone="ok" />
                  <ListUI.Item layout="stat" title="Păstrate" meta={reviewStats.skipped} />
                  <ListUI.Item layout="stat" title="Erori" meta={reviewStats.errors} tone={reviewStats.errors ? "bad" : undefined} />
               </ListUI>

               <ButtonUI.Row align="start">
                  <ButtonUI
                     variant="success"
                     confirm="Da, confirmă tot"
                     confirmTitle={isTextRepair ? "Se actualizează textele RO și RU; ordinea răspunsurilor rămâne." : "Româna rămâne neschimbată."}
                     loading={bulkApplying}
                     disabled={!pendingItems.length || previewLoading || busy}
                     onClick={applyAll}
                  >
                     Confirmă tot ({pendingItems.length})
                  </ButtonUI>
                  {bulkApplying && (
                     <ButtonUI variant="danger" onClick={stopRun}>
                        Oprește
                     </ButtonUI>
                  )}
               </ButtonUI.Row>

               <ChoiceUI
                  look="chips"
                  aria-label="Biletul"
                  value={selectedTicket}
                  onChange={setSelectedTicket}
                  options={ticketTabs.map((code) => ({ value: code, label: code === "all" ? "Toate" : code }))}
               />

               {currentItem ? (
                  <>
                     <ButtonUI.Row
                        align="split"
                        leading={
                           <ButtonUI onClick={() => setCurrentIndex((value) => value - 1)} disabled={currentIndex <= 0 || busy}>
                              Înapoi
                           </ButtonUI>
                        }
                     >
                        <TagUI tone="page">
                           Întrebarea {currentIndex + 1} din {visiblePreviewItems.length}
                        </TagUI>
                        <ButtonUI
                           onClick={() => setCurrentIndex((value) => value + 1)}
                           disabled={currentIndex >= visiblePreviewItems.length - 1 || busy}
                        >
                           Următoarea
                        </ButtonUI>
                     </ButtonUI.Row>
                     <PreviewCard item={currentItem} decision={currentDecision} />
                     <ButtonUI.Row>
                        <ButtonUI
                           variant="primary"
                           loading={itemBusy}
                           onClick={applyCurrent}
                           disabled={!currentReviewable || busy || currentDecision === DECISION.APPLIED}
                        >
                           Schimbăm
                        </ButtonUI>
                        <ButtonUI
                           variant="subtle"
                           onClick={skipCurrent}
                           disabled={busy || currentDecision === DECISION.APPLIED || currentDecision === DECISION.SKIPPED}
                        >
                           Nu schimbăm
                        </ButtonUI>
                     </ButtonUI.Row>
                  </>
               ) : (
                  <ListUI>
                     <ListUI.Empty>Nu există întrebări pentru biletul ales.</ListUI.Empty>
                  </ListUI>
               )}
            </>
         )}

         <ImportLog rows={applyResults} />
      </CardUI>
   );
}
